// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// SVG scalebar renderer — screen-fixed substitutions-per-site scale bar.

import { el, rgba } from "./dom.js";

const LOG10 = Math.log(10);

/**
 * Compute the numeric metrics for the scalebar label and geometry.
 *
 *   value = barSpan / (scale * branchScale)
 *   minDigits = parseInt(Math.abs(Math.log(value) / LOG10), 10)   (base-10 magnitude)
 *   digits = minDigits + props.scalebarDigits
 *   label = value.toFixed(digits)
 *
 * @param {number} scale - Current view scale (svg.clientWidth / viewBox.w).
 * @param {number} branchScale - graph.branchScale (pixels-per-substitution in tree coords).
 * @param {object} props - Props including scalebarWidth, scalebarPadding, scalebarDigits, scalebar.
 * @returns {{ value: number, digits: number, label: string } | null}
 *   Returns null when scalebar should not be shown.
 */
export function scalebarMetrics(scale, branchScale, props) {
  if (props.scalebar === false) return null;
  if (!Number.isFinite(branchScale) || branchScale <= 0) return null;
  if (!Number.isFinite(scale) || scale <= 0) return null;

  const width = props.scalebarWidth ?? 96;
  const padding = props.scalebarPadding ?? 4;
  const digitsExtra = props.scalebarDigits ?? 2;

  const barSpan = width - 2 * padding;
  // A non-positive bar span (misconfigured width <= 2*padding) makes value <= 0,
  // which would feed Math.log a non-positive number and yield a NaN label.
  if (barSpan <= 0) return null;

  const value = barSpan / (scale * branchScale);
  const minDigits = parseInt(Math.abs(Math.log(value) / LOG10), 10);
  // Clamp to Number.prototype.toFixed's valid range [0, 100]; only reachable
  // with pathological branch scales, but an out-of-range argument throws.
  const digits = Math.min(100, minDigits + digitsExtra);
  const label = value.toFixed(digits);

  return { value, digits, label };
}

/**
 * Render a screen-fixed scalebar as a <g class="scalebar"> element.
 *
 * The scalebar is a direct child of the <svg> root (sibling of contentGroup).
 * It is NOT inside the zoom transform. Screen-fixed appearance is achieved by
 * recomputing its coordinates in viewBox user-units on every view change:
 *
 *   scale = svg.clientWidth / viewBox.w
 *   To place at screen offset (sx, sy) from viewport top-left:
 *     viewBox coord x = viewBox.x + sx / scale
 *     viewBox coord y = viewBox.y + sy / scale
 *
 * Positioning rule (position.left + position.bottom with defaults 0):
 *   anchorX (screen) = scalebarPosition.left  (0 = left edge)
 *   anchorY (screen) = clientHeight - scalebarHeight - scalebarPosition.bottom
 *
 * Returns null when scalebarMetrics returns null (scalebar disabled or
 * branchScale invalid).
 *
 * @param {object} graph - Built graph (graph.branchScale).
 * @param {object} props - Props.
 * @param {number} scale - svg.clientWidth / viewBox.w.
 * @param {{ x: number, y: number, w: number, h: number }} viewBox - Current viewBox state.
 * @returns {SVGGElement|null}
 */
export function renderScalebar(graph, props, scale, viewBox) {
  const metrics = scalebarMetrics(scale, graph ? graph.branchScale : null, props);
  if (!metrics) return null;

  const { label } = metrics;

  const sbWidth   = props.scalebarWidth ?? 96;
  const sbHeight  = props.scalebarHeight ?? 24;
  const sbPadding = props.scalebarPadding ?? 4;
  const sbLineWidth = props.scalebarLineWidth ?? 1;
  const sbFontSize  = props.scalebarFontSize ?? 13;
  const sbPosition  = props.scalebarPosition ?? { bottom: 0, left: 0 };
  const sbFillColour   = props.scalebarFillColour ?? [34, 34, 34];
  const sbStrokeColour = props.scalebarStrokeColour ?? [34, 34, 34];
  const sbBackground   = props.scalebarBackground ?? [255, 255, 255];
  const sbOpacity      = props.scalebarOpacity ?? 0.87;

  // Screen-space anchor (top-left of the scalebar bounding box).
  // position.left: distance from left edge in screen px.
  // position.bottom: distance from bottom edge in screen px.
  const screenLeft   = sbPosition.left ?? 0;
  // clientHeight / scale == viewBox.h
  const screenBottom = sbPosition.bottom ?? 0;

  // Convert screen coords to viewBox coords.
  // anchorX = left edge of scalebar box in viewBox units
  const anchorX = viewBox.x + screenLeft / scale;
  // anchorY = top edge of the scalebar box in viewBox units
  // box top = clientHeight - sbHeight - screenBottom (in screen px)
  // in viewBox units: viewBox.y + (clientHeight - sbHeight - screenBottom) / scale
  //   but clientHeight / scale = viewBox.h, so:
  const anchorY = viewBox.y + viewBox.h - (sbHeight + screenBottom) / scale;

  // Bar geometry in viewBox units. The bar sits near the box top and the value
  // hangs one scalebarPadding below it (dominant-baseline "hanging"), so the
  // value clears the bar by the same margin the box clears its own edges and
  // both stay inside the sbHeight box. (Was: bar near the box bottom with the
  // value hanging straight off it, which grazed the digits and overflowed the
  // box bottom.)
  const lineY   = anchorY + (sbPadding + sbLineWidth) / scale;
  const lineX1  = anchorX + (sbPadding + sbLineWidth) / scale;
  const lineX2  = anchorX + (sbWidth - sbPadding - sbLineWidth) / scale;
  const textX   = anchorX + (sbWidth / 2) / scale;
  const textY   = lineY + sbPadding / scale;

  // Background rect
  const bgRect = el("rect", {
    x: anchorX,
    y: anchorY,
    width: sbWidth / scale,
    height: sbHeight / scale,
    fill: rgba(sbBackground, true),
    "fill-opacity": sbOpacity,
    stroke: "none",
  });

  // Horizontal bar line
  const line = el("line", {
    x1: lineX1,
    y1: lineY,
    x2: lineX2,
    y2: lineY,
    stroke: rgba(sbStrokeColour, true),
    "stroke-width": sbLineWidth / scale,
    "stroke-linecap": "round",
  });

  // Label text
  const text = el(
    "text",
    {
      x: textX,
      y: textY,
      "text-anchor": "middle",
      "dominant-baseline": "hanging",
      fill: rgba(sbFillColour, true),
      "font-size": sbFontSize / scale,
      "font-family": "monospace",
    },
    [label],
  );

  return el("g", { class: "scalebar" }, [bgRect, line, text]);
}
